// src/utils/workflowPipeline.ts
// ─────────────────────────────────────────────────────────────────
// Cross-module integration layer — Phase 1
//
// This module provides the functions that make the backbone pipeline
// actually work as a connected system:
//
//   Drawing → DrawingTask → WorkInstruction → ConfiguredTask
//                       ↓
//              CanonicalBOMLine → RFQ → PO → GRN
//                       ↓
//             ComplianceGate → MaterialsReady
//                       ↓
//              WI Steps Completed → MilestoneEligibility
//                       ↓
//              ProgressClaim Evidence → Handover Checklist
//
// Previously every one of these "→" was a gap. This module builds
// the bridges. Each function resolves a real ID into a real record.
// ─────────────────────────────────────────────────────────────────

import {
  getAllBOMLines, getBOMForJob, getBOMLinesByDrawingTask,
  computeMaterialsReadiness, checkRFQComplianceGate,
  type CanonicalBOMLine, type MaterialsReadiness, type RFQComplianceGate,
} from './canonicalBOM'
import { MOCK_TASKS, type DrawingTask } from './mockDataDrawings'
import { MOCK_JOB_PACKAGES, type WorkInstruction } from './mockDataWorkInstructions'
import { MOCK_CONFIGURED_TASKS, MOCK_MOBILE_TASK_RECORDS, type ConfiguredTask } from './mockDataMobile'
import { MOCK_RFQS, type RFQ, type RFQLineItem } from './mockDataRFQ'
import { MOCK_POS } from './mockData'
import {
  getCompletionsForWI, isHoldPointCleared, getWICompletionSummary, pullLiveEvidence,
  type WICompletionSummary, type LiveEvidenceItem,
} from './wiStepCompletion'

// ── 1. DrawingTask resolution ────────────────────────────────────

export function resolveDrawingTask(drawingTaskId: string): DrawingTask | null {
  return MOCK_TASKS.find(t => t.id === drawingTaskId) ?? null
}

export function getWorkInstructionsForDrawingTask(drawingTaskId: string): WorkInstruction[] {
  return MOCK_JOB_PACKAGES.filter(wi => wi.drawingTaskId === drawingTaskId)
}

export function getConfiguredTasksForWorkInstruction(workInstructionId: string): ConfiguredTask[] {
  return MOCK_CONFIGURED_TASKS.filter(ct => ct.workInstructionId === workInstructionId)
}

/** Full pipeline trace for one drawing task */
export function traceTaskPipeline(drawingTaskId: string): {
  drawingTask: DrawingTask | null
  workInstructions: WorkInstruction[]
  configuredTasks: ConfiguredTask[]
  bomLines: CanonicalBOMLine[]
  materialsReady: MaterialsReadiness
} {
  const drawingTask = resolveDrawingTask(drawingTaskId)
  const workInstructions = getWorkInstructionsForDrawingTask(drawingTaskId)
  const configuredTasks = workInstructions.flatMap(wi => getConfiguredTasksForWorkInstruction(wi.id))
  const bomLines = getBOMLinesByDrawingTask(drawingTaskId)
  const materialsReady = computeMaterialsReadiness(drawingTaskId)

  return { drawingTask, workInstructions, configuredTasks, bomLines, materialsReady }
}

// ── 2. WI completion — delegated to wiStepCompletion.ts ──────────
// Use getWICompletionSummary() from wiStepCompletion.ts directly.
// Re-exported here for convenience.
export type { WICompletionSummary, LiveEvidenceItem } from './wiStepCompletion'
export { getWICompletionSummary, pullLiveEvidence } from './wiStepCompletion'

// ── 3. Milestone eligibility computation ─────────────────────────
// This is the function that DIDN'T EXIST anywhere else in the codebase.
// It replaces hand-typed Milestone.status / blockingItems.

export type BlockingReason = {
  type: 'WI_INCOMPLETE' | 'HOLD_POINT_OPEN' | 'PO_NOT_PAID' | 'BOM_NOT_RECEIVED' | 'COMPLIANCE_FAIL'
  reference: string
  description: string
}

export type MilestoneEligibilityResult = {
  eligible: boolean
  blockingReasons: BlockingReason[]
  wiStatuses: WICompletionSummary[]
  bomReadiness: MaterialsReadiness[]
}

export function computeMilestoneEligibility(milestone: {
  linkedWIs?: string[]
  linkedITP?: string[]
  linkedPOs?: string[]
  linkedDrawingTaskIds?: string[]
  wiIdMap?: Record<string, string>
}): MilestoneEligibilityResult {
  const blockingReasons: BlockingReason[] = []
  const wiStatuses: WICompletionSummary[] = []
  const bomReadiness: MaterialsReadiness[] = []

  // Check linked Work Instructions using live step completion store
  ;(milestone.linkedWIs ?? []).forEach(wiRef => {
    const wi = MOCK_JOB_PACKAGES.find(w => w.reference === wiRef || w.id === wiRef)
    if (!wi) return

    const holdPointRefs = wi.steps
      .filter(s => s.isHoldPoint && s.holdPointRef)
      .map(s => s.holdPointRef!)

    // Use wiIdMap if provided, otherwise fall back to wi.id
    const wiId = milestone.wiIdMap?.[wiRef] ?? wi.id
    const summary = getWICompletionSummary(wiId, wi.reference, wi.steps.length, holdPointRefs)
    wiStatuses.push(summary)

    if (!summary.isComplete) {
      blockingReasons.push({
        type: 'WI_INCOMPLETE',
        reference: wi.reference,
        description: `Work instruction "${wi.title}" — ${summary.stepsCompleted}/${summary.stepsTotal} steps complete`,
      })
    }

    // Check each hold point individually via the clearance store
    holdPointRefs.forEach(ref => {
      if (!isHoldPointCleared(ref)) {
        blockingReasons.push({
          type: 'HOLD_POINT_OPEN',
          reference: ref,
          description: `Hold point "${ref}" not yet cleared in ${wi.reference}`,
        })
      }
    })
  })

  // Also check ITP hold points not tied to specific WIs
  ;(milestone.linkedITP ?? []).forEach(hpRef => {
    if (!isHoldPointCleared(hpRef)) {
      // Only add if not already added via WI check
      if (!blockingReasons.find(b => b.reference === hpRef && b.type === 'HOLD_POINT_OPEN')) {
        blockingReasons.push({
          type: 'HOLD_POINT_OPEN',
          reference: hpRef,
          description: `ITP hold point "${hpRef}" not yet cleared`,
        })
      }
    }
  })

  // Check linked POs delivered/paid
  ;(milestone.linkedPOs ?? []).forEach(poRef => {
    const po = (MOCK_POS as any[]).find((p: any) => p.poNumber === poRef || p.id === poRef)
    if (!po) return
    if (!['DELIVERED', 'PAID', 'COMPLETED'].includes(po.status)) {
      blockingReasons.push({
        type: 'PO_NOT_PAID',
        reference: po.poNumber ?? po.id,
        description: `PO ${po.poNumber} not delivered/paid (status: ${po.status})`,
      })
    }
  })

  // Check BOM materials readiness for linked drawing tasks
  ;(milestone.linkedDrawingTaskIds ?? []).forEach(taskId => {
    const readiness = computeMaterialsReadiness(taskId)
    bomReadiness.push(readiness)
    if (!readiness.ready) {
      readiness.lines.filter(l => !l.ready).forEach(l => {
        blockingReasons.push({
          type: l.reason?.toLowerCase().includes('compliance') ? 'COMPLIANCE_FAIL' : 'BOM_NOT_RECEIVED',
          reference: l.line.id,
          description: `"${l.line.description}" — ${l.reason}`,
        })
      })
    }
  })

  return { eligible: blockingReasons.length === 0, blockingReasons, wiStatuses, bomReadiness }
}

// ── 4. RFQ compliance gate ───────────────────────────────────────

export function getRFQComplianceStatus(rfqId: string): RFQComplianceGate {
  const rfq = MOCK_RFQS.find(r => r.id === rfqId)
  if (!rfq) return { canSend: false, blockingLines: [{ bomLineId: '', description: 'RFQ not found', flag: 'NEEDS_REVIEW', reason: 'RFQ not found' }], warningLines: [] }
  return checkRFQComplianceGate(rfq.lineItems.map(l => ({ id: l.id, bomLineId: l.bomLineId ?? undefined })))
}

// ── 5. Job-level materials dashboard (PM cross-job view) ─────────

export type JobMaterialsSummary = {
  jobId: string
  jobNumber: string
  totalLines: number
  onSite: number
  inTransit: number
  notOrdered: number
  complianceIssues: number
  pooledLines: number
  readyForWork: boolean  // all BUILD_MATERIAL lines on site and compliant
}

export function getJobMaterialsSummary(jobId: string, jobNumber: string): JobMaterialsSummary {
  const lines = getBOMForJob(jobId).filter(l => l.materialType === 'BUILD_MATERIAL')
  const onSite = lines.filter(l => l.stockStatus === 'ON_SITE').length
  const inTransit = lines.filter(l => l.stockStatus === 'IN_TRANSIT' || l.stockStatus === 'WAREHOUSE').length
  const notOrdered = lines.filter(l => l.procurementStatus === 'NOT_ORDERED').length
  const complianceIssues = lines.filter(l => !l.complianceVerifiedForThisJob || l.complianceFlag !== 'COMPLIANT').length
  const pooledLines = lines.filter(l => l.source === 'POOLED_IN' || l.source === 'RETURNED').length
  const readyForWork = lines.length > 0 && onSite === lines.length && complianceIssues === 0

  return { jobId, jobNumber, totalLines: lines.length, onSite, inTransit, notOrdered, complianceIssues, pooledLines, readyForWork }
}

// ── 6. Claim evidence builder — delegated to wiStepCompletion.ts ─
// pullLiveEvidence() is exported from wiStepCompletion.ts directly.
// Import it from there; see also ProgressPaymentClaims.tsx for usage.

// ── 7. Handover checklist computation ────────────────────────────

export type HandoverChecklistItem = {
  name: string
  status: 'COMPLETE' | 'IN_PROGRESS' | 'NOT_STARTED' | 'BLOCKED'
  computedNote: string
  isComputed: boolean   // true = derived from real data; false = manual
  blockedBy?: string
}

/** Compute the real status of handover checklist items that have a source-of-truth module */
export function computeHandoverChecklist(jobId: string): HandoverChecklistItem[] {
  const jobWIs = MOCK_JOB_PACKAGES.filter(wi => wi.applicableJobIds?.includes(jobId))
  const totalWIs = jobWIs.length

  // Use live completion store for real step completion data
  const wiSummaries = jobWIs.map(wi => {
    const holdPointRefs = wi.steps.filter(s => s.isHoldPoint && s.holdPointRef).map(s => s.holdPointRef!)
    return getWICompletionSummary(wi.id, wi.reference, wi.steps.length, holdPointRefs)
  })
  const completedWIs = wiSummaries.filter(s => s.isComplete).length
  const totalOpenHPs = wiSummaries.reduce((acc, s) => acc + (s.holdPointsTotal - s.holdPointsCleared), 0)

  const bomLines = getBOMForJob(jobId)
  const nonCompliantLines = bomLines.filter(l => !l.complianceVerifiedForThisJob || l.complianceFlag !== 'COMPLIANT')
  const receivedLines = bomLines.filter(l => l.materialType === 'BUILD_MATERIAL' && l.stockStatus === 'ON_SITE')
  const totalBuildLines = bomLines.filter(l => l.materialType === 'BUILD_MATERIAL').length

  return [
    {
      name: 'Work instructions — all steps complete',
      status: completedWIs === totalWIs && totalWIs > 0 ? 'COMPLETE' : totalWIs === 0 ? 'NOT_STARTED' : 'IN_PROGRESS',
      computedNote: totalWIs === 0 ? 'No work instructions linked to this job' : `${completedWIs} of ${totalWIs} work instructions complete`,
      isComputed: true,
    },
    {
      name: 'ITP — all hold points released (evidence)',
      status: totalOpenHPs === 0 && totalWIs > 0 ? 'COMPLETE' : totalOpenHPs > 0 ? 'IN_PROGRESS' : 'NOT_STARTED',
      computedNote: totalOpenHPs === 0
        ? `All ${wiSummaries.reduce((s, w) => s + w.holdPointsTotal, 0)} hold point(s) cleared`
        : `${totalOpenHPs} hold point(s) still open`,
      isComputed: true,
      blockedBy: totalOpenHPs > 0 ? `${totalOpenHPs} hold point(s) not yet cleared` : undefined,
    },
    {
      name: 'BOM materials — all compliant with client specs',
      status: nonCompliantLines.length === 0 && bomLines.length > 0 ? 'COMPLETE' : nonCompliantLines.length > 0 ? 'BLOCKED' : 'NOT_STARTED',
      computedNote: nonCompliantLines.length === 0
        ? `All ${bomLines.length} BOM lines verified compliant`
        : `${nonCompliantLines.length} line(s) with compliance issues: ${nonCompliantLines.map(l => l.description.split(' ').slice(0, 3).join(' ')).join(', ')}`,
      isComputed: true,
      blockedBy: nonCompliantLines.length > 0 ? 'Compliance verification required' : undefined,
    },
    {
      name: 'Build materials — all received on site',
      status: totalBuildLines === 0 ? 'NOT_STARTED' : receivedLines.length === totalBuildLines ? 'COMPLETE' : receivedLines.length > 0 ? 'IN_PROGRESS' : 'NOT_STARTED',
      computedNote: `${receivedLines.length} of ${totalBuildLines} build material line${totalBuildLines !== 1 ? 's' : ''} confirmed received on site`,
      isComputed: true,
    },
    {
      name: 'As-built drawings — issued to client',
      status: 'NOT_STARTED',
      computedNote: 'Manual — upload and mark complete when issued',
      isComputed: false,
    },
    {
      name: 'Form 16 (or equivalent) — Certificate of Inspection',
      status: 'NOT_STARTED',
      computedNote: 'Manual — issued by authorised person, enter reference number',
      isComputed: false,
    },
    {
      name: 'Defects liability schedule — confirmed with client',
      status: 'NOT_STARTED',
      computedNote: 'Manual — confirm DLP start date and schedule with client representative',
      isComputed: false,
    },
    {
      name: 'Site cleaned and demobilised',
      status: 'NOT_STARTED',
      computedNote: 'Manual — PM/PC to confirm site clearance',
      isComputed: false,
    },
  ]
}
